import { EQUIPMENT_PROCESS_TYPES, normalizeEquipmentProcessType } from './equipmentConstants';
import type { EquipmentProcessType } from './equipmentTypes';

export const CUSTOM_PROCESS_TYPES_METADATA_KEY = 'customProcessTypes';

function canonical(value: unknown): EquipmentProcessType | null {
  const candidate = String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_')
    .replace(/[^A-Z0-9_]/g, '');
  if (!/[A-Z0-9]/.test(candidate)) return null;
  return normalizeEquipmentProcessType(candidate);
}

export function readCustomProcessTypes(
  projectMetadata: Record<string, unknown> | null | undefined
): EquipmentProcessType[] {
  const raw = projectMetadata?.[CUSTOM_PROCESS_TYPES_METADATA_KEY];
  if (!Array.isArray(raw)) return [];
  const builtIns = new Set<string>(EQUIPMENT_PROCESS_TYPES);
  return [
    ...new Set(
      raw
        .map(canonical)
        .filter((value): value is EquipmentProcessType => Boolean(value))
        .filter((value) => !builtIns.has(value))
    ),
  ];
}

export function buildProcessTypeCatalog(
  projectMetadata: Record<string, unknown> | null | undefined,
  observedValues: readonly unknown[] = []
): EquipmentProcessType[] {
  const values = [
    ...EQUIPMENT_PROCESS_TYPES,
    ...readCustomProcessTypes(projectMetadata),
    ...observedValues
      .map(canonical)
      .filter((value): value is EquipmentProcessType => Boolean(value)),
  ];
  return [...new Set(values)];
}

export function addCustomProcessType(
  projectMetadata: Record<string, unknown> | null | undefined,
  value: unknown
): { processType: EquipmentProcessType; customProcessTypes: EquipmentProcessType[] } | null {
  const processType = canonical(value);
  if (!processType) return null;
  const builtIns = new Set<string>(EQUIPMENT_PROCESS_TYPES);
  const customProcessTypes = builtIns.has(processType)
    ? readCustomProcessTypes(projectMetadata)
    : [...new Set([...readCustomProcessTypes(projectMetadata), processType])];
  return { processType, customProcessTypes };
}
